import {
  canParent,
  OBJECTIVE_HORIZON_LABELS,
  OBJECTIVE_HORIZONS,
  OBJECTIVE_LEVEL_LABELS,
  OBJECTIVE_LEVELS,
  OBJECTIVE_RELATION_LABELS,
  OBJECTIVE_RELATION_TYPES,
  OBJECTIVE_STATUS_LABELS,
  OBJECTIVE_STATUSES,
  formatDate,
  objectiveInputSchema,
  objectiveListSchema,
  objectiveOptionsSchema,
  objectiveSchema,
  type Objective,
  type ObjectiveInput,
  type ObjectiveLevelName,
  type ObjectiveOptions,
  type ObjectiveRelationTypeName,
  type ObjectiveStatusName,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  CalendarRange,
  FolderKanban,
  Lock,
  Link2,
  MessageSquare,
  Network,
  Pencil,
  Plus,
  Search,
  Target,
  Trash2,
  UserRound,
  Users,
  ClipboardList,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { TabList, tabPanelProps } from '@/components/ui/tabs';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { ObjectiveAnalyses } from '@/features/meetings/CriticalAnalysis';
import { OBJECTIVE_STATUS_LIGHT } from '@/features/objectives/status';
import { DecisionHistoryList } from '@/features/meetings/Minutes';
import { DRILL_PARAMS, drillHref, readId, readPeriod } from '@/features/drill/drill';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const SERIES = ['bg-series-1', 'bg-series-2', 'bg-series-3', 'bg-series-4', 'bg-series-5', 'bg-series-6'];

const PAGE_SIZE = 50;
type ObjectiveTab = 'collab' | 'analyses' | 'decisions';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Estratégia › Objetivos (T-041, RF-18): lista por perspectiva, filtros, cadastro e arquivo. */
export function ObjetivosPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Objetivos estratégicos"
        description="O que a empresa precisa alcançar em cada perspectiva do BSC, quem responde por isso e como os objetivos se conectam."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os objetivos pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <ObjectivesSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function ObjectivesSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  /** Projeto arquivado: só consulta. */
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/objectives`;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(() => params.get('q')?.trim().slice(0, 100) ?? '');
  const [q, setQ] = useState(search);
  // A perspectiva do filtro fica no endereço (o número de uma perspectiva na Visão Geral chega com ela — drill-down,
  // T-144c); o mês do link segue para o detalhe dos objetivos.
  const perspectiveId = readId(params, DRILL_PARAMS.perspective) ?? '';
  const setPerspectiveId = (id: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set(DRILL_PARAMS.perspective, id);
        else next.delete(DRILL_PARAMS.perspective);
        return next;
      },
      { replace: true },
    );
  const period = readPeriod(params);
  const [departmentId, setDepartmentId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [status, setStatus] = useState<ObjectiveStatusName | ''>('');
  const [archived, setArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Objective | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Objective | null>(null);
  /**
   * Objetivo aberto com os comentários e anexos (T-113d; link da notificação: ?objetivo=) e, para quem vê a
   * Análise Crítica, a aba "Análises críticas" (T-121c, RN-28).
   */
  const [discussing, setDiscussing] = useState<{ id: string; name: string; tab: ObjectiveTab } | null>(null);
  const seesAnalyses = canDo('analise-critica', 'view');
  const tabsId = useId();
  const [linkedId, setLinkedId] = useState(() => {
    const raw = params.get('objetivo') ?? '';
    return UUID.test(raw) ? raw : '';
  });
  // Lido o objetivo do link, ele sai do endereço (trocar de projeto ou recarregar não reabre).
  useEffect(() => {
    if (!params.has('objetivo')) return;
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('objetivo');
        return out;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const linked = useQuery({
    queryKey: ['objectives', projectId, 'linked', linkedId],
    queryFn: ({ signal }) => client.request(`${base}/${linkedId}`, { schema: objectiveSchema, signal }),
    enabled: Boolean(linkedId),
    meta: { handlesError: true },
  });
  useEffect(() => {
    if (!linkedId) return;
    if (linked.data) {
      setDiscussing({ id: linked.data.id, name: linked.data.name, tab: 'collab' });
      setLinkedId('');
    } else if (linked.isError) {
      toast.error('Não foi possível abrir o objetivo', { description: errorMessage(linked.error) });
      setLinkedId('');
    }
  }, [linkedId, linked.data, linked.isError, linked.error]);
  const ids = { search: useId(), perspective: useId(), department: useId(), owner: useId(), status: useId() };

  useEffect(() => {
    const next = search.trim();
    if (next === q) return;
    const timer = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, q]);

  const options = useQuery({
    queryKey: ['objectives', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: objectiveOptionsSchema, signal }),
  });
  const filters = { q, perspectiveId, departmentId, ownerId, status, archived, page };
  const list = useQuery({
    queryKey: ['objectives', projectId, 'list', filters],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: {
          q: q || undefined,
          perspectiveId: perspectiveId || undefined,
          departmentId: departmentId || undefined,
          ownerId: ownerId || undefined,
          status: status || undefined,
          archived: archived ? 'true' : undefined,
          page,
          pageSize: PAGE_SIZE,
        },
        schema: objectiveListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['objectives', projectId] });

  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (o: Objective) =>
      client.request(`${base}/${o.id}/${o.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: objectiveSchema,
      }),
    onSuccess: async (o) => {
      setArchiving(null);
      toast.success(o.archivedAt ? 'Objetivo arquivado' : 'Objetivo restaurado', { description: o.name });
      await refresh();
    },
    onError: (err) => {
      setArchiving(null);
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    },
  });

  async function save(input: ObjectiveInput) {
    const target = editing === 'new' ? null : editing;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: objectiveSchema,
    });
    setEditing(null);
    toast.success(target ? 'Objetivo salvo' : 'Objetivo criado', { description: saved.name });
    await refresh();
  }

  const filtered = Boolean(q || perspectiveId || departmentId || ownerId || status);
  const opts = options.data;
  // Perspectiva do endereço que não é deste projeto (link antigo, troca de projeto): sai do filtro.
  const unknownPerspective =
    Boolean(perspectiveId) && Boolean(opts) && !opts!.perspectives.some((p) => p.id === perspectiveId);
  useEffect(() => {
    if (!unknownPerspective) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(DRILL_PARAMS.perspective);
        return next;
      },
      { replace: true },
    );
  }, [unknownPerspective, setParams]);
  const colorOf = (id: string) =>
    SERIES[Math.max(0, opts?.perspectives.findIndex((p) => p.id === id) ?? 0) % SERIES.length]!;

  const groups = (() => {
    const items = list.data?.items ?? [];
    const map = new Map<string, { perspective: Objective['perspective']; items: Objective[] }>();
    for (const o of items) {
      const g = map.get(o.perspective.id) ?? { perspective: o.perspective, items: [] };
      g.items.push(o);
      map.set(o.perspective.id, g);
    }
    return [...map.values()];
  })();

  return (
    <div className="mt-6 flex flex-col gap-5">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: os objetivos ficam disponíveis só para consulta.
        </p>
      ) : null}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={ids.search} className="sr-only">
              Buscar objetivo
            </label>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={ids.search}
              type="search"
              placeholder="Buscar pelo nome"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-9"
            />
          </div>
          {can('objetivos', 'create') && !archived ? (
            <Button className="h-11" onClick={() => setEditing('new')} disabled={!opts}>
              <Plus aria-hidden />
              Novo objetivo
            </Button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Filter
            id={ids.perspective}
            label="Perspectiva"
            value={perspectiveId}
            onChange={(v) => {
              setPerspectiveId(v);
              setPage(1);
            }}
          >
            <option value="">Todas as perspectivas</option>
            {opts?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.department}
            label="Área"
            value={departmentId}
            onChange={(v) => {
              setDepartmentId(v);
              setPage(1);
            }}
          >
            <option value="">Todas as áreas</option>
            {opts?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.status}
            label="Situação"
            value={status}
            onChange={(v) => {
              setStatus(v as ObjectiveStatusName | '');
              setPage(1);
            }}
          >
            <option value="">Todas as situações</option>
            {OBJECTIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {OBJECTIVE_STATUS_LABELS[s]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.owner}
            label="Responsável"
            value={ownerId}
            onChange={(v) => {
              setOwnerId(v);
              setPage(1);
            }}
          >
            <option value="">Todos os responsáveis</option>
            {opts?.owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-(--brand)"
            checked={archived}
            onChange={(e) => {
              setArchived(e.target.checked);
              setPage(1);
            }}
          />
          Ver só os arquivados
        </label>
      </div>

      {list.isPending ? (
        <LoadingState rows={5} label="Carregando os objetivos" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.total === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Target}
          title={
            filtered
              ? 'Nenhum objetivo encontrado'
              : archived
                ? 'Nenhum objetivo arquivado'
                : 'Nenhum objetivo ainda'
          }
          description={
            filtered
              ? 'Nenhum objetivo corresponde à busca e aos filtros escolhidos.'
              : 'Cadastre os objetivos estratégicos de cada perspectiva: o que a empresa precisa alcançar no horizonte do projeto.'
          }
          action={
            filtered ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setPerspectiveId('');
                  setDepartmentId('');
                  setOwnerId('');
                  setStatus('');
                }}
              >
                Limpar filtros
              </Button>
            ) : can('objetivos', 'create') && !archived ? (
              <Button onClick={() => setEditing('new')} disabled={!opts}>
                <Plus aria-hidden />
                Novo objetivo
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-6" aria-busy={list.isFetching}>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 objetivo' : `${list.data.total} objetivos`}
          </p>
          {groups.map((g) => (
            <section key={g.perspective.id} aria-label={g.perspective.name}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-bold">
                <span className={cn('size-2.5 rounded-full', colorOf(g.perspective.id))} aria-hidden />
                {g.perspective.name}
                <span className="font-normal text-muted-foreground">({g.items.length})</span>
              </h2>
              <ul aria-label={`Objetivos de ${g.perspective.name}`} className="flex flex-col gap-2">
                {g.items.map((o) => (
                  <li key={o.id} className="rounded-lg border border-border bg-surface p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={drillHref.objective(organizationId, {
                              projectId,
                              objectiveId: o.id,
                              year: period?.year,
                              month: period?.month,
                            })}
                            className="font-semibold underline-offset-2 hover:underline"
                          >
                            {o.name}
                          </Link>
                          <StatusBadge
                            status={OBJECTIVE_STATUS_LIGHT[o.status]}
                            label={OBJECTIVE_STATUS_LABELS[o.status]}
                          />
                        </div>
                        {o.description ? (
                          <p className="mt-1 text-sm text-muted-foreground">{o.description}</p>
                        ) : null}
                        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <Meta
                            icon={UserRound}
                            label="Responsável"
                            value={o.owner?.name ?? 'Sem responsável'}
                          />
                          <Meta icon={Users} label="Área" value={o.department?.name ?? 'Sem área'} />
                          <Meta
                            icon={Network}
                            label="Desdobramento"
                            value={
                              o.parent
                                ? `${OBJECTIVE_LEVEL_LABELS[o.level]} · desdobra ${o.parent.name}`
                                : OBJECTIVE_LEVEL_LABELS[o.level]
                            }
                          />
                          <Meta
                            icon={CalendarRange}
                            label="Período"
                            value={
                              o.startDate || o.endDate
                                ? `${o.startDate ? formatDate(o.startDate) : '…'} a ${o.endDate ? formatDate(o.endDate) : '…'}`
                                : OBJECTIVE_HORIZON_LABELS[o.horizon]
                            }
                          />
                          {o.weight !== null ? (
                            <Meta icon={Target} label="Peso" value={`${o.weight}%`} />
                          ) : null}
                          {o.relations.length + o.relatedFrom.length ? (
                            <Meta
                              icon={Link2}
                              label="Relações"
                              value={`${o.relations.length + o.relatedFrom.length} ${o.relations.length + o.relatedFrom.length === 1 ? 'relação' : 'relações'}`}
                            />
                          ) : null}
                        </dl>
                      </div>
                      <div className="flex gap-1 sm:shrink-0">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Comentários e anexos de ${o.name}`}
                          onClick={() => setDiscussing({ id: o.id, name: o.name, tab: 'collab' })}
                        >
                          <MessageSquare aria-hidden />
                        </Button>
                        {seesAnalyses ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Análises críticas de ${o.name}`}
                            onClick={() => setDiscussing({ id: o.id, name: o.name, tab: 'analyses' })}
                          >
                            <ClipboardList aria-hidden />
                          </Button>
                        ) : null}
                        {!o.archivedAt && can('objetivos', 'edit') ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Editar ${o.name}`}
                            onClick={() => setEditing(o)}
                            disabled={!opts}
                          >
                            <Pencil aria-hidden />
                          </Button>
                        ) : null}
                        {can('objetivos', 'delete') ? (
                          o.archivedAt ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={toggleArchive.isPending}
                              onClick={() => toggleArchive.mutate(o)}
                            >
                              <ArchiveRestore aria-hidden />
                              Restaurar
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Arquivar ${o.name}`}
                              onClick={() => setArchiving(o)}
                            >
                              <Archive aria-hidden />
                            </Button>
                          )
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação de objetivos" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </div>
      )}

      <Dialog
        open={discussing !== null}
        onOpenChange={(open) => !open && setDiscussing(null)}
        title={discussing?.name ?? 'Comentários e anexos'}
        className="w-[min(42rem,calc(100vw-2rem))]"
      >
        {discussing ? (
          <div className="flex flex-col gap-4">
            {seesAnalyses ? (
              <TabList
                id={tabsId}
                label={`Seções de ${discussing.name}`}
                tabs={[
                  { key: 'collab', label: 'Comentários e anexos' },
                  { key: 'analyses', label: 'Análises críticas' },
                  { key: 'decisions', label: 'Decisões' },
                ]}
                value={discussing.tab}
                onChange={(tab) => setDiscussing({ ...discussing, tab })}
              />
            ) : null}
            {discussing.tab === 'decisions' && seesAnalyses ? (
              <div {...tabPanelProps(tabsId, 'decisions')}>
                <DecisionHistoryList
                  organizationId={organizationId}
                  projectId={projectId}
                  subject="objectives"
                  subjectId={discussing.id}
                  label={discussing.name}
                />
              </div>
            ) : discussing.tab === 'analyses' && seesAnalyses ? (
              <div {...tabPanelProps(tabsId, 'analyses')}>
                <ObjectiveAnalyses
                  organizationId={organizationId}
                  projectId={projectId}
                  objectiveId={discussing.id}
                />
              </div>
            ) : (
              <div {...(seesAnalyses ? tabPanelProps(tabsId, 'collab') : {})}>
                <CollaborationPanel
                  organizationId={organizationId}
                  projectId={projectId}
                  subject="objective"
                  subjectId={discussing.id}
                  label={discussing.name}
                />
              </div>
            )}
          </div>
        ) : null}
      </Dialog>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Novo objetivo' : 'Editar objetivo'}
        className="w-[min(42rem,calc(100vw-2rem))]"
      >
        {editing !== null && opts ? (
          <ObjectiveForm
            initial={editing === 'new' ? undefined : editing}
            options={opts}
            defaultPerspectiveId={perspectiveId || undefined}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar o objetivo?"
        description={`${archiving?.name ?? ''} sai das listas e do mapa. O histórico e as relações ficam guardados, e você pode restaurá-lo depois.`}
        confirmLabel="Arquivar objetivo"
        destructive
        busy={toggleArchive.isPending}
        onConfirm={() => archiving && toggleArchive.mutate(archiving)}
      />
    </div>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        Filtrar por {label.toLowerCase()}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}

function Meta({ icon: Icon, label, value }: { icon: typeof Target; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1">
      <Icon className="size-3.5" aria-hidden />
      <dt className="sr-only">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Formulário completo do objetivo (também usado no quadro da Construção do BSC). */
export function ObjectiveForm({
  initial,
  options,
  defaultPerspectiveId,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  initial?: Objective;
  options: ObjectiveOptions;
  defaultPerspectiveId?: string;
  onSubmit: (input: ObjectiveInput) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [perspectiveId, setPerspectiveId] = useState(
    initial?.perspective.id ?? defaultPerspectiveId ?? options.perspectives[0]?.id ?? '',
  );
  const [status, setStatus] = useState<ObjectiveStatusName>(initial?.status ?? 'PLANNED');
  const [ownerId, setOwnerId] = useState(initial?.owner?.id ?? '');
  const [departmentId, setDepartmentId] = useState(initial?.department?.id ?? '');
  const [horizon, setHorizon] = useState(initial?.horizon ?? 'MEDIUM');
  const [level, setLevel] = useState<ObjectiveLevelName>(initial?.level ?? 'CORPORATE');
  const [parentId, setParentId] = useState(initial?.parent?.id ?? '');
  const [startDate, setStartDate] = useState(initial?.startDate ?? '');
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [weight, setWeight] = useState(
    initial?.weight === null || initial?.weight === undefined ? '' : String(initial.weight),
  );
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [relations, setRelations] = useState<{ objectiveId: string; type: ObjectiveRelationTypeName }[]>(
    initial?.relations.map((r) => ({ objectiveId: r.objective.id, type: r.type })) ?? [],
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    name: useId(),
    description: useId(),
    perspective: useId(),
    status: useId(),
    owner: useId(),
    department: useId(),
    horizon: useId(),
    level: useId(),
    parent: useId(),
    start: useId(),
    end: useId(),
    weight: useId(),
    notes: useId(),
    relations: useId(),
  };

  // Pessoa ou área atual que saiu das opções ativas continua escolhível (salvar não troca).
  const owners =
    initial?.owner && !options.owners.some((o) => o.id === initial.owner!.id)
      ? [...options.owners, initial.owner]
      : options.owners;
  const departments =
    initial?.department && !options.departments.some((d) => d.id === initial.department!.id)
      ? [...options.departments, { ...initial.department, parentId: null }]
      : options.departments;
  // Objetivos que podem entrar numa relação nova: os ativos do projeto (horizontal: só do mesmo nível).
  const relatable = options.objectives.filter((o) => o.id !== initial?.id);
  const relatableFor = (type: ObjectiveRelationTypeName) =>
    type === 'HORIZONTAL' ? relatable.filter((o) => o.level === level) : relatable;
  // Pais possíveis: objetivos de nível acima (o atual, mesmo arquivado, continua na lista).
  const parents = relatable.filter((o) => canParent(o.level, level));
  const keptParent =
    initial?.parent && !parents.some((o) => o.id === initial.parent!.id) && parentId === initial.parent.id
      ? initial.parent
      : null;
  // Relação que já existia com um objetivo hoje arquivado: continua valendo (o histórico fica), só na
  // linha em que já estava e com o mesmo tipo.
  const keptArchived = (r: { objectiveId: string; type: ObjectiveRelationTypeName }) =>
    initial?.relations.find(
      (x) => x.objective.archived && x.objective.id === r.objectiveId && x.type === r.type,
    )?.objective;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = objectiveInputSchema.safeParse({
      name,
      description,
      perspectiveId,
      status,
      ownerId: ownerId || null,
      departmentId: departmentId || null,
      horizon,
      level,
      parentId: level === 'CORPORATE' ? null : parentId || null,
      startDate: startDate || null,
      endDate: endDate || null,
      weight: weight.trim() === '' ? null : weight.replace(',', '.'),
      notes,
      relations: relations.filter((r) => r.objectiveId),
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      // O objetivo vai inteiro (PUT substitui): campo esvaziado vai como null.
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={160}
          placeholder="Ex.: Aumentar a receita recorrente"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={2}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.perspective} label="Perspectiva" error={errors.perspectiveId}>
          <NativeSelect
            {...fieldA11y(ids.perspective, errors.perspectiveId)}
            value={perspectiveId}
            onChange={(e) => setPerspectiveId(e.target.value)}
          >
            {initial && !options.perspectives.some((p) => p.id === initial.perspective.id) ? (
              <option value={initial.perspective.id}>{initial.perspective.name} (arquivada)</option>
            ) : null}
            {options.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.status} label="Situação" error={errors.status}>
          <NativeSelect
            {...fieldA11y(ids.status, errors.status)}
            value={status}
            onChange={(e) => setStatus(e.target.value as ObjectiveStatusName)}
          >
            {OBJECTIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {OBJECTIVE_STATUS_LABELS[s]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.owner} label="Responsável executivo" error={errors.ownerId}>
          <NativeSelect
            {...fieldA11y(ids.owner, errors.ownerId)}
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">Sem responsável</option>
            {owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField
          id={ids.department}
          label={level === 'CORPORATE' ? 'Área responsável' : 'Área responsável (obrigatória)'}
          error={errors.departmentId}
        >
          <NativeSelect
            {...fieldA11y(ids.department, errors.departmentId)}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Sem área (empresa toda)</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.level} label="Nível no desdobramento" error={errors.level}>
          <NativeSelect
            {...fieldA11y(ids.level, errors.level)}
            value={level}
            onChange={(e) => {
              const next = e.target.value as ObjectiveLevelName;
              setLevel(next);
              // O pai precisa continuar de nível acima; corporativo não tem pai.
              const current = relatable.find((o) => o.id === parentId);
              if (next === 'CORPORATE' || (current && !canParent(current.level, next))) setParentId('');
              // Relação horizontal nova com objetivo de outro nível deixa de valer: a linha fica sem alvo.
              setRelations((all) =>
                all.map((r) =>
                  r.type === 'HORIZONTAL' &&
                  !keptArchived(r) &&
                  !relatable.some((o) => o.id === r.objectiveId && o.level === next)
                    ? { ...r, objectiveId: '' }
                    : r,
                ),
              );
            }}
          >
            {OBJECTIVE_LEVELS.map((l) => (
              <option key={l} value={l}>
                {OBJECTIVE_LEVEL_LABELS[l]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        {level === 'CORPORATE' ? (
          <div className="self-end pb-3">
            <p className="text-xs text-muted-foreground">
              Corporativo é o topo do desdobramento. Diretoria e gerência pertencem a uma área e podem
              desdobrar um objetivo de nível acima.
            </p>
            {errors.parentId ? (
              <p role="alert" className="mt-1.5 text-sm font-medium text-status-red-fg">
                {errors.parentId}
              </p>
            ) : null}
          </div>
        ) : (
          <FormField id={ids.parent} label="Desdobra o objetivo (pai)" error={errors.parentId}>
            <NativeSelect
              {...fieldA11y(ids.parent, errors.parentId)}
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">
                {initial?.parentHidden ? 'Mantém o atual (de área fora do seu acesso)' : 'Sem objetivo-pai'}
              </option>
              {keptParent ? (
                <option value={keptParent.id}>
                  {keptParent.name}
                  {keptParent.archived ? ' (arquivado)' : ''}
                </option>
              ) : null}
              {parents.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} · {OBJECTIVE_LEVEL_LABELS[o.level]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        )}
        <FormField id={ids.horizon} label="Horizonte" error={errors.horizon}>
          <NativeSelect
            {...fieldA11y(ids.horizon, errors.horizon)}
            value={horizon}
            onChange={(e) => setHorizon(e.target.value as typeof horizon)}
          >
            {OBJECTIVE_HORIZONS.map((h) => (
              <option key={h} value={h}>
                {OBJECTIVE_HORIZON_LABELS[h]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.weight} label="Peso na perspectiva (%)" error={errors.weight}>
          <Input
            {...fieldA11y(ids.weight, errors.weight)}
            value={weight}
            inputMode="decimal"
            maxLength={6}
            onChange={(e) => setWeight(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.start} label="Data inicial" error={errors.startDate}>
          <Input
            {...fieldA11y(ids.start, errors.startDate)}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.end} label="Data final" error={errors.endDate}>
          <Input
            {...fieldA11y(ids.end, errors.endDate)}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-11"
          />
        </FormField>
      </div>

      <fieldset aria-describedby={`${ids.relations}-dica`}>
        <legend className="text-sm font-medium">Objetivos relacionados</legend>
        <p id={`${ids.relations}-dica`} className="mt-1 text-xs text-muted-foreground">
          Causa e efeito: este objetivo contribui para o outro. Horizontal: objetivos do mesmo nível, de áreas
          que andam juntas. O desdobramento vertical é o objetivo-pai, acima.
        </p>
        {relations.length ? (
          <ul className="mt-2 flex flex-col gap-2">
            {relations.map((r, i) => (
              <li key={i} className="flex flex-col gap-2 sm:flex-row">
                <div className="sm:w-48">
                  <NativeSelect
                    aria-label={`Tipo da relação ${i + 1}`}
                    value={r.type}
                    disabled={Boolean(keptArchived(r))}
                    onChange={(e) =>
                      setRelations((all) =>
                        all.map((x, j) => {
                          if (j !== i) return x;
                          const type = e.target.value as ObjectiveRelationTypeName;
                          // O alvo escolhido só fica se continuar elegível para o novo tipo.
                          const keep = relatableFor(type).some((o) => o.id === x.objectiveId);
                          return { type, objectiveId: keep ? x.objectiveId : '' };
                        }),
                      )
                    }
                  >
                    {OBJECTIVE_RELATION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {OBJECTIVE_RELATION_LABELS[t]}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <div className="flex flex-1 gap-2">
                  <div className="flex-1">
                    <NativeSelect
                      aria-label={`Objetivo da relação ${i + 1}`}
                      value={r.objectiveId}
                      onChange={(e) =>
                        setRelations((all) =>
                          all.map((x, j) => (j === i ? { ...x, objectiveId: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">Escolha o objetivo</option>
                      {keptArchived(r) ? (
                        <option value={r.objectiveId}>{keptArchived(r)!.name} (arquivado)</option>
                      ) : null}
                      {relatableFor(r.type).map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remover relação ${i + 1}`}
                    onClick={() => setRelations((all) => all.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {errors.relations ? (
          <p className="mt-1.5 text-sm font-medium text-status-red-fg">{errors.relations}</p>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={relatable.length === 0}
          onClick={() => setRelations((all) => [...all, { objectiveId: '', type: 'CAUSE_EFFECT' }])}
        >
          <Link2 aria-hidden />
          {relatable.length === 0 ? 'Nenhum outro objetivo para relacionar' : 'Adicionar relação'}
        </Button>
      </fieldset>

      <FormField id={ids.notes} label="Observações (opcional)" error={errors.notes}>
        <textarea
          {...fieldA11y(ids.notes, errors.notes)}
          value={notes}
          rows={2}
          maxLength={2000}
          onChange={(e) => setNotes(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar objetivo' : 'Criar objetivo'}
        </Button>
      </div>
    </form>
  );
}
